import { createFormHook, useStore } from '@tanstack/react-form';
import { Button } from './button';
import type React from 'react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { fieldContext, formContext, useFormContext } from './form/shared';
import { FormReadOnlyProvider, useFormReadOnly } from './form/form-read-only';
import { FormOptionalLabelProvider } from './form/form-optional-label';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Skeleton } from './skeleton';
import { Spinner } from './spinner';
export type { OtherImageItem } from './form/multi-image-upload-field';

// Eager: small, frequently used fields that should be in the base form chunk.
import TextField from './form/text-field';
import TextareaField from './form/textarea-field';
import NumberField from './form/number-field';
import CurrencyField from './form/currency-field';
import SuffixTextField from './form/suffix-text-field';
import CheckboxField from './form/checkbox-field';
import SwitchField from './form/switch-field';
import PasswordField from './form/password-field';
import LinkTextField from './form/link-text-field';

// Skeleton for fields laid out as `grid gap-2` + FieldLabelRow (h-5) + control.
// Total height matches a real field: 20 (label row) + 8 (gap) + control height.
// `w-full` on the wrapper so in horizontal flex layouts the skeleton takes the
// same width as the real field (which receives `w-full` from the parent).
function StandardFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
	return (
		<div className="grid w-full gap-2">
			<div className="flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('h-8 w-full rounded-lg', controlClassName)} />
		</div>
	);
}

// Skeleton for fields laid out as `mb-2` between label and a tall control
// (upload drop zones, editor body). Matches their stacked layout exactly.
function StackedFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
	return (
		<div className="w-full">
			<div className="mb-2 flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('w-full rounded-xl', controlClassName)} />
		</div>
	);
}

// Upload fields render very differently depending on read-only state:
// - Editing: a ~198px drop zone.
// - Read-only: either an empty "No file" line (~20px) or a compact file chip
//   row (~58px). Pick the skeleton size based on context to avoid CLS.
function UploadFieldSkeleton({ editingClassName = 'h-[198px]' }: { editingClassName?: string } = {}) {
	const isReadOnly = useFormReadOnly();
	return (
		<div className="w-full">
			<div className="mb-2 flex h-5 items-center">
				<Skeleton className="h-3.5 w-24" />
			</div>
			<Skeleton className={cn('w-full rounded-xl', isReadOnly ? 'h-12' : editingClassName)} />
		</div>
	);
}

// LanguagesPickerField renders a flat list of rows (no label of its own).
// Each row is h-14; there are 11 supported languages in availableLanguages.
const LANGUAGES_PICKER_ROWS = ['SPA', 'ENG', 'FRA', 'DEU', 'ITA', 'POR', 'CAT', 'EUS', 'GLG', 'NLD', 'PRT'] as const;

function LanguagesPickerFieldSkeleton() {
	return (
		<>
			{LANGUAGES_PICKER_ROWS.map((code) => (
				<Skeleton key={code} className="h-14 w-full rounded-lg" />
			))}
		</>
	);
}

// Wrap a lazy field with a Suspense boundary so every parent form gets a
// per-field skeleton for free while the chunk is loading.
function lazyField<P extends object>(
	loader: () => Promise<{ default: React.ComponentType<P> }>,
	fallback: React.ReactNode = <StandardFieldSkeleton />,
): React.ComponentType<P> {
	const LazyComponent = lazy(loader);
	return function LazyField(props: P) {
		return (
			<Suspense fallback={fallback}>
				<LazyComponent {...props} />
			</Suspense>
		);
	};
}

// Each field is wrapped in its own Suspense boundary with a skeleton fallback
// sized to match the resolved field, avoiding CLS.
const EditorField = lazyField(() => import('./form/editor-field'), <StackedFieldSkeleton controlClassName="h-48" />);
const SimpleEditorField = lazyField(
	() => import('./form/simple-editor-field'),
	<StackedFieldSkeleton controlClassName="h-32" />,
);
const SelectTextField = lazyField(() => import('./form/select-text-field'));
const SelectNumberField = lazyField(() => import('./form/select-number-field'));
const AltTextField = lazyField(() => import('./form/alt-text-field'));
const DateField = lazyField(() => import('./form/date-field'));
const DateTimeField = lazyField(() => import('./form/datetime-field'));
const ColorField = lazyField(() => import('./form/color-field'));
const FileField = lazyField(() => import('./form/file-field'));
const ImageField = lazyField(() => import('./form/image-field'));
// DropZone renders ~198px (py-10 + icon + text + button lg) when editing;
// UploadFieldSkeleton shrinks to match the compact read-only render.
const ImageUploadField = lazyField(() => import('./form/image-upload-field'), <UploadFieldSkeleton />);
const MultiImageUploadField = lazyField(() => import('./form/multi-image-upload-field'), <UploadFieldSkeleton />);
const VideoUploadField = lazyField(() => import('./form/video-upload-field'), <UploadFieldSkeleton />);
const DocumentUploadField = lazyField(() => import('./form/document-upload-field'), <UploadFieldSkeleton />);
// SdgField is a Combobox chips field (min-h-8), laid out with mb-2 label.
const SdgField = lazyField(() => import('./form/sdg-field'), <StackedFieldSkeleton controlClassName="h-8" />);
const LanguagesPickerField = lazyField(() => import('./form/languages-picker-field'), <LanguagesPickerFieldSkeleton />);
const CountryField = lazyField(() => import('./form/country-field'));
const MultiCountryField = lazyField(() => import('./form/multi-country-field'));
const RegionField = lazyField(() => import('./form/region-field'));
const MultiCountryRegionField = lazyField(() => import('./form/multi-country-region-field'));
const ProvinceField = lazyField(() => import('./form/province-field'));
const ComboboxTextField = lazyField(() => import('./form/combobox-text-field'));
const ComboboxNumberField = lazyField(() => import('./form/combobox-number-field'));
const MultiComboboxTextField = lazyField(() => import('./form/multi-combobox-text-field'));
const MultiComboboxNumberField = lazyField(() => import('./form/multi-combobox-number-field'));
const LockableTextField = lazyField(() => import('./form/lockable-text-field'));
const SlugField = lazyField(() => import('./form/slug-field'));

export const MAX_SIZE = 4 * 1024 * 1024;

export const downloadFile = (blob: Blob, filename: string) => {
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const fileToBase64 = async (file: File | undefined): Promise<string | undefined> =>
	file instanceof File
		? new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				const handleLoad = () => resolve(reader.result as string);
				reader.addEventListener('load', handleLoad);
				reader.addEventListener('error', reject);
			})
		: undefined;

export const downloadCsvTemplate = (headers: string[], fileName: string) => {
	const csv = headers.join(',');
	// Send charset utf-8 as a header in the download, is the only way to make utf-8 work on excel
	// See https://stackoverflow.com/a/18925211/6261347
	const blob = new Blob(['\ufeff', csv]);
	const downloadLink = document.createElement('a');
	const url = URL.createObjectURL(blob);
	downloadLink.href = url;
	downloadLink.download = `${fileName}.csv`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

export const getIdOrGenerate = (id: string | undefined): string => (!id || id === '' ? crypto.randomUUID() : id);

function SubmitButton({ label, ...props }: React.ComponentProps<typeof Button> & { label: string }) {
	const form = useFormContext();
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Button {...props} disabled={props.disabled ?? isSubmitting}>
					{isSubmitting && <Spinner />}
					{label}
				</Button>
			)}
		</form.Subscribe>
	);
}

function SaveDiscardButtons(props: {
	className?: string;
	unsavedChangesText?: string;
	discardText?: string;
	saveText?: string;
	invalidText?: string;
}) {
	const form = useFormContext();
	const { isSubmitting, isDefaultValue, canSubmit } = useStore(form.store, (state) => ({
		isDefaultValue: state.isDefaultValue,
		isSubmitting: state.isSubmitting,
		canSubmit: state.canSubmit,
	}));

	// A plain submit silently does nothing when the form is invalid, and inline
	// field errors stay hidden until a field is touched, so a Save click on an
	// untouched-but-invalid form (e.g. a legacy record loaded with bad data) gives
	// no feedback. Mark every field touched to reveal the errors, scroll to the
	// first one, and explain why the save was blocked.
	const handleInvalidSave = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (canSubmit) return;
		event.preventDefault();
		for (const name of Object.keys(form.state.fieldMeta)) {
			form.setFieldMeta(name as never, (meta) => ({ ...meta, isTouched: true }));
		}
		const formElement = event.currentTarget.closest('form');
		requestAnimationFrame(() => {
			formElement?.querySelector('[data-slot="form-message"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
		});
		toast.error(props.invalidText ?? 'Please review the highlighted fields before saving.');
	};

	const unsavedText = props.unsavedChangesText ?? 'You have unsaved changes, are you sure you want to leave?';

	useBlocker({
		shouldBlockFn: () => {
			if (isDefaultValue) return false;

			const shouldLeave = confirm(unsavedText);
			return !shouldLeave;
		},
		enableBeforeUnload: !isDefaultValue,
	});

	if (isDefaultValue) return null;

	return (
		<div className={cn('flex gap-2', props.className)}>
			<Button
				type="reset"
				variant="secondary"
				onClick={(event) => {
					event.preventDefault();
					form.reset();
					// TODO: We need to double reset to remove all translated campaign texts with the translate feature,
					// is totally a bug on tanstack/form
					setTimeout(() => form.reset());
				}}
			>
				{props.discardText ?? 'Discard'}
			</Button>
			<Button disabled={isSubmitting} onClick={handleInvalidSave}>
				{isSubmitting && <Spinner />}
				{props.saveText ?? 'Save'}
			</Button>
		</div>
	);
}

export { FormReadOnlyProvider, useFormReadOnly, FormOptionalLabelProvider };

const { useAppForm: _useAppForm, withForm } = createFormHook({
	fieldComponents: {
		TextField,
		TextareaField,
		NumberField,
		CurrencyField,
		DateField,
		DateTimeField,
		ColorField,
		FileField,
		ImageField,
		SelectTextField,
		SelectNumberField,
		CountryField,
		MultiCountryField,
		MultiCountryRegionField,
		RegionField,
		ProvinceField,
		ComboboxTextField,
		ComboboxNumberField,
		MultiComboboxTextField,
		MultiComboboxNumberField,
		CheckboxField,
		SwitchField,
		PasswordField,
		SuffixTextField,
		EditorField,
		SimpleEditorField,
		ImageUploadField,
		DocumentUploadField,
		VideoUploadField,
		MultiImageUploadField,
		AltTextField,
		LinkTextField,
		LockableTextField,
		SlugField,
		SdgField,
		LanguagesPickerField,
	},
	formComponents: {
		SubmitButton,
		SaveDiscardButtons,
	},
	fieldContext,
	formContext,
});

function useAppFormWithScrollToError(...args: Parameters<typeof _useAppForm>): ReturnType<typeof _useAppForm> {
	const form = _useAppForm(...args);

	const submissionAttempts = useStore(form.store, (state: { submissionAttempts: number }) => state.submissionAttempts);
	const prevAttempts = useRef(0);
	useEffect(() => {
		if (submissionAttempts > prevAttempts.current) {
			prevAttempts.current = submissionAttempts;
			setTimeout(() => {
				const firstError = document.querySelector<HTMLElement>('[aria-invalid="true"]');
				if (firstError) {
					firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
					firstError.focus({ preventScroll: true });
				}
			}, 0);
		}
	}, [submissionAttempts]);

	return form;
}

export const useAppForm = useAppFormWithScrollToError as typeof _useAppForm;
export { withForm };
